import React, { useContext, useEffect, useRef, useState } from 'react'
import { Chip, Card, Box } from '@mui/material'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

import { faPlus } from '@fortawesome/free-solid-svg-icons/faPlus'
import { faTimes } from '@fortawesome/free-solid-svg-icons/faTimes'
import { faExclamationCircle } from '@fortawesome/free-solid-svg-icons/faExclamationCircle'

import LoadingDefault from '@/Components/Elements/Loading/LoadingDefault'
import QueryBuilderCombinator from './QueryCombinator'
import { Link } from 'react-router-dom'
import { KeDoContextType, QueryBuilderLinkProps } from './types'
import { KeDoContext } from '../../../kedo-context'
import {
    CompactButton,
    StyledButton,
} from '@/Components/Elements/StyledComponents/Buttons'
import { brandedTokens } from '@/theme/tokens'

const QueryBuilderLink: React.FC<QueryBuilderLinkProps> = ({
    field,
    level: propLevel,
    defdossier,
    defdossierlink,
    ddObject,
    index,
    onChange,
    onDelete,
    linkField = null,
}) => {
    const kedo = useContext<KeDoContextType>(KeDoContext)

    const [name, setName] = useState('Link')
    const [uuid] = useState(field.uuid)

    const [combinators, setCombinators] = useState(
        field && field.combinators ? field.combinators : []
    )

    const [where_min, setWhereMin] = useState(
        field && field.where_min ? field.where_min : null
    )
    const [where_empty_not_empty, setWhereEmptyNotEmpty] = useState(
        field && field.where_empty_not_empty
            ? field.where_empty_not_empty
            : null
    )
    const [include_archived, setIncludeArchived] = useState(
        field && field.include_archived ? field.include_archived : null
    )
    const [where_max, setWhereMax] = useState(
        field && field.where_max ? field.where_max : null
    )
    const [where_other_type, setWhereOtherType] = useState(
        field && field.where_other_type ? field.where_other_type : null
    )
    const [where_other_query, setWhereOtherQuery] = useState(
        field && field.where_other_query ? field.where_other_query : null
    )

    const [sellink, setSellink] = useState(null)
    const [seldata, setSeldata] = useState(null)

    const [dataFields, setDataFields] = useState<any[]>([])
    const [linkFields, setLinkFields] = useState<any[]>([])
    const [queries, setQueries] = useState<any[]>([])

    const [loadingDataFieldItems, setLoadingDataFieldItems] = useState(false)
    const [loadingLinkFields, setLoadingLinkFields] = useState(false)
    const [loadingQueryItems, setLoadingQueryItems] = useState(false)

    const [error, setError] = useState<number | null>(null)

    const level = propLevel ?? 0

    const fetchDataFields = (ddId: any) => {
        const params = { params: { defDossier: ddId, limit: 500 } }
        setLoadingDataFieldItems(true)
        kedo.api()
            .getCached(kedo.api().getDefDossierDefFieldEndpoint(), params)
            .then((response: any) => setDataFields(response.data.results))
            .finally(() => setLoadingDataFieldItems(false))
    }

    const fetchQueries = (ddId: any) => {
        const params = { params: { defDossier: ddId, limit: 500 } }
        setLoadingQueryItems(true)
        kedo.api()
            .get(kedo.api().getDossierQueryEndpoint(), params)
            .then((response: any) => setQueries(response.data.results))
            .finally(() => setLoadingQueryItems(false))
    }

    const fetchLinkFields = (ddId: any) => {
        const params = {
            params: {
                defDossier: ddId,
                environment: kedo.env().getEnvironment().id,
                limit: 500,
            },
        }
        setLoadingLinkFields(true)
        kedo.api()
            .get(kedo.api().getDefDossierLinkEndpoint(), params)
            .then((response: any) => setLinkFields(response.data.results))
            .finally(() => setLoadingLinkFields(false))
    }

    const fetchFields = () => {
        if (defdossier) {
            fetchDataFields(defdossier)
            fetchLinkFields(defdossier)
        } else {
            const ddlUrl = kedo.api().getDefDossierLinkEndpoint()
            kedo.api()
                .get(ddlUrl + '/' + defdossierlink)
                .then((response: any) => {
                    setName(response.data.name)
                    fetchDataFields(response.data.child_def_dossier_id)
                    fetchLinkFields(response.data.child_def_dossier_id)
                    fetchQueries(response.data.child_def_dossier_id)
                })
                .catch((err: any) => {
                    setError(err.response.status)
                })
        }
    }

    useEffect(() => {
        fetchFields()
    }, [])

    const fieldRef = useRef(field)
    fieldRef.current = field

    useEffect(() => {
        const link: any = {
            ...fieldRef.current,
            uuid,
            combinators,
            where_min,
            where_max,
            where_other_query,
            where_other_type,
            where_empty_not_empty,
            include_archived,
        }
        if (defdossierlink != null && defdossierlink !== '') {
            link.id = defdossierlink
        }
        onChange(index, link)
    }, [
        uuid,
        combinators,
        where_min,
        where_max,
        where_other_query,
        where_other_type,
        where_empty_not_empty,
        include_archived,
        index,
        defdossierlink,
    ])

    const onChangeCombinator = (
        combinatorIndex: number,
        combinatorField: any
    ) => {
        setCombinators((prev: any[]) =>
            prev.map((c, i) => (i === combinatorIndex ? combinatorField : c))
        )
    }

    const onDeleteCombinator = (uuidToDelete: string) => {
        setCombinators((prev: any[]) =>
            prev.filter((item: any) => item.uuid !== uuidToDelete)
        )
    }

    const addCombinator = () => {
        setCombinators((prev: any[]) => [
            ...prev,
            { id: defdossierlink, uuid: kedo.utils().uuidv4() },
        ])
    }

    const onNormalChange = (
        e: React.ChangeEvent<HTMLSelectElement | HTMLInputElement>
    ) => {
        const { name, value } = e.target
        let v: any = value

        if (name === 'where_min' || name === 'where_max') {
            v = value === '' ? null : Number(value)
        }

        if (name === 'where_empty_not_empty' || name === 'include_archived') {
            v = value === '' ? null : value
        }

        const validFields = [
            'where_min',
            'where_max',
            'where_other_query',
            'where_other_type',
            'where_empty_not_empty',
            'include_archived',
        ]
        if (!validFields.includes(name)) return

        switch (name) {
            case 'where_min':
                setWhereMin(v)
                break
            case 'where_max':
                setWhereMax(v)
                break
            case 'where_other_query':
                setWhereOtherQuery(v)
                break
            case 'where_other_type':
                setWhereOtherType(v)
                break
            case 'where_empty_not_empty':
                setWhereEmptyNotEmpty(v)
                break
            case 'include_archived':
                setIncludeArchived(v)
                break
        }
    }

    return (
        <Card
            variant="outlined"
            sx={{
                mb: 2,
                borderRadius: 1,
                borderColor:
                    index % 2 === 0
                        ? brandedTokens.variables.primary
                        : brandedTokens.variables.secondary,
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    alignItems: 'center',
                    gap: 2,
                    p: 2,
                    borderBottom: 1,
                    borderColor: 'divider',
                    backgroundColor: brandedTokens.variables.primary,
                }}
            >
                <Chip
                    label={`${kedo.t('Link')} ${
                        ddObject ? ddObject.name_plural : ''
                    } ${name}`}
                    color="info"
                    variant="filled"
                />

                {error && error === 404 && (
                    <Chip
                        label={`${kedo.t('Item not found')}`}
                        color="warning"
                        variant="filled"
                        icon={<FontAwesomeIcon icon={faExclamationCircle} />}
                    />
                )}

                {error && error !== 404 && (
                    <Chip
                        label={`${kedo.t('Something went wrong')}`}
                        color="warning"
                        variant="filled"
                        icon={<FontAwesomeIcon icon={faExclamationCircle} />}
                    />
                )}

                {loadingDataFieldItems ||
                loadingLinkFields ||
                loadingQueryItems ? (
                    <LoadingDefault as={'span'} />
                ) : null}

                <Box
                    sx={{
                        '& select': {
                            display: 'inline-block',
                            width: 'auto',
                            fontSize: '0.875rem',
                            padding: '0.25rem 0.5rem',
                            lineHeight: 1.5,
                            borderRadius: '0.2rem',
                        },
                        '& label': {
                            fontSize: '0.875rem',
                            fontWeight: 500,
                            marginRight: '0.5rem',
                        },
                    }}
                >
                    <label>{kedo.t('empty/not-empty')}</label>
                    <select
                        onChange={onNormalChange}
                        value={where_empty_not_empty ?? ''}
                        name="where_empty_not_empty"
                    >
                        <option value={''}>&nbsp;</option>
                        <option value={'EMPTY'}>{kedo.t('EMPTY')}</option>
                        <option value={'NOT_EMPTY'}>
                            {kedo.t('NOT EMPTY')}
                        </option>
                    </select>
                </Box>

                <Box
                    sx={{
                        '& select': {
                            display: 'inline-block',
                            width: 'auto',
                            fontSize: '0.875rem',
                            padding: '0.25rem 0.5rem',
                            lineHeight: 1.5,
                            borderRadius: '0.2rem',
                        },
                        '& label': {
                            fontSize: '0.875rem',
                            fontWeight: 500,
                            mr: 1,
                            mb: 0,
                        },
                    }}
                >
                    <label>{kedo.t('Archived')}</label>
                    <select
                        onChange={onNormalChange}
                        value={include_archived ?? ''}
                        name="include_archived"
                    >
                        <option value={''}>{kedo.t('Both')}</option>
                        <option value={'none_archived'}>
                            {kedo.t('None archived')}
                        </option>
                        <option value={'archived'}>{kedo.t('Archived')}</option>
                    </select>
                </Box>

                <Box sx={{ visibility: 'hidden', display: 'inline-block' }}>
                    <label>{kedo.t('Minimum')}</label>
                    <input
                        value={where_min}
                        onChange={onNormalChange}
                        type="text"
                        name="where_min"
                    />
                </Box>

                <Box sx={{ visibility: 'hidden', display: 'inline-block' }}>
                    <label>{kedo.t('Maximum')}</label>
                    <input
                        value={where_max}
                        onChange={onNormalChange}
                        type="text"
                        name="where_max"
                    />
                </Box>

                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        '& select': {
                            display: 'inline-block',
                            width: 'auto',
                            fontSize: '0.875rem',
                            padding: '0.25rem 0.5rem',
                            lineHeight: 1.5,
                            borderRadius: '0.2rem',
                        },
                        '& label': {
                            fontSize: '0.875rem',
                            fontWeight: 500,
                            mr: 1,
                            mb: 0,
                        },
                    }}
                >
                    <label>{kedo.t('Where other query')}</label>

                    <select
                        onChange={onNormalChange}
                        value={where_other_type}
                        name="where_other_type"
                    >
                        <option value={''}>&nbsp;</option>
                        <option value={'IN'}>{kedo.t('IN')}</option>
                        <option value={'NOT_IN'}>{kedo.t('NOT IN')}</option>
                    </select>

                    <select
                        onChange={onNormalChange}
                        value={where_other_query}
                        name="where_other_query"
                    >
                        <option value={''}>&nbsp;</option>
                        {queries.map((queryItem: any) => (
                            <option key={queryItem.id} value={queryItem.id}>
                                {kedo
                                    .env()
                                    .translateItem(queryItem, 'dossierquery')}
                            </option>
                        ))}
                    </select>

                    {where_other_query && (
                        <Link
                            to={`/querybuilder/${where_other_query}`}
                            target="_blank"
                        >
                            <StyledButton color={'secondary'} dense>
                                View
                            </StyledButton>
                        </Link>
                    )}
                </Box>
                <Box sx={{ display: 'flex', ml: 'auto' }}>
                    <CompactButton dense onClick={addCombinator}>
                        <FontAwesomeIcon icon={faPlus} />
                    </CompactButton>
                    <CompactButton
                        dense
                        color={'secondary'}
                        onClick={() => onDelete(uuid)}
                    >
                        <FontAwesomeIcon icon={faTimes} />
                    </CompactButton>
                </Box>
            </Box>

            <Box sx={{ p: 1 }}>
                {combinators.map((field: any, linkIndex: number) => (
                    <QueryBuilderCombinator
                        field={field}
                        kedo={kedo}
                        level={level + 1}
                        key={field.uuid}
                        uuid={field.uuid}
                        defdossierlink={field.id}
                        index={linkIndex}
                        onDelete={onDeleteCombinator}
                        onChange={onChangeCombinator}
                        linkField={linkFields.find(
                            (linkItem: any) => linkItem.id === field.id
                        )}
                    />
                ))}
            </Box>
        </Card>
    )
}

export default QueryBuilderLink
